import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Instagram, Save } from "lucide-react";

const InstagramRewardsSettingsCard = () => {
  const { toast } = useToast();
  const [instagramAutoApprove, setInstagramAutoApprove] = useState(false);
  const [igRequiredHashtag, setIgRequiredHashtag] = useState("#Inparadisebeauty");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    supabase
      .from("site_settings")
      .select("key, value")
      .in("key", ["instagram_auto_approve", "ig_required_hashtag"])
      .then(({ data }) => {
        data?.forEach((s: any) => {
          if (s.key === "instagram_auto_approve") setInstagramAutoApprove(s.value === "true");
          if (s.key === "ig_required_hashtag" && s.value) setIgRequiredHashtag(s.value);
        });
        setLoading(false);
      });
  }, []);

  const upsertSetting = async (key: string, value: string) => {
    const { data: existing } = await supabase.from("site_settings").select("id").eq("key", key).maybeSingle();
    if (existing) {
      await supabase.from("site_settings").update({ value, updated_at: new Date().toISOString() }).eq("id", existing.id);
    } else {
      await supabase.from("site_settings").insert({ key, value });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        upsertSetting("instagram_auto_approve", instagramAutoApprove ? "true" : "false"),
        upsertSetting("ig_required_hashtag", igRequiredHashtag),
      ]);
      toast({ title: "Saved", description: "Instagram reward settings updated." });
    } catch (err: any) {
      toast({ title: "Save failed", description: err?.message || "Unable to save", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-card border-border/50 max-w-xl">
      <CardHeader>
        <CardTitle className="text-sm font-light tracking-[0.3em] uppercase text-muted-foreground flex items-center gap-2">
          <Instagram size={16} />
          Instagram Link Approval
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-muted-foreground text-sm font-light leading-relaxed">
          When clients submit an Instagram post link to earn a $5 coupon, you can require admin approval before the coupon is generated.
        </p>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border border-border/50 bg-background">
          <div className="min-w-0">
            <p className="text-sm font-medium">Auto-Approve All Links</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {instagramAutoApprove
                ? "Coupons are generated instantly when clients submit a link"
                : "Links go to admin review queue — coupons are generated on approval"}
            </p>
          </div>
          <Switch checked={instagramAutoApprove} onCheckedChange={setInstagramAutoApprove} className="shrink-0" />
        </div>
        <div>
          <Label className="text-xs tracking-wide uppercase text-muted-foreground">Required Hashtag</Label>
          <Input
            value={igRequiredHashtag}
            onChange={(e) => setIgRequiredHashtag(e.target.value)}
            placeholder="#Inparadisebeauty"
            className="bg-background border-border/50 mt-1"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Clients must include this hashtag in their Instagram post to be eligible for credit.
          </p>
        </div>
        <p className="text-xs text-muted-foreground">
          You can also bypass approval for individual clients in the Clients page.
        </p>
        <Button onClick={handleSave} disabled={saving || loading} className="text-xs tracking-widest uppercase">
          <Save size={14} className="mr-2" />
          {saving ? "Saving…" : "Save Settings"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default InstagramRewardsSettingsCard;
